// 3-5-8 online (owner 2026-10-29: 6 deals, 20 s a decision, 30 s for the 4 cards laid away): a store with the offline
// store's interface (358/store.ts), fed by the server's frames (server/ws/cards/three-five-eight.js), so Table358.tsx
// and its score window work unchanged. The deal, each exchange and a full trick are animated from the change between
// two states (the musik and the cards laid away by the view itself); the server waits for each of them.
import { legalCards, sameCard, type HCard, type State358, type SwapDone } from '../358/rules';
import { COLLECT_MS, DEAL_MS, SWAP_FLY_MS, TRICK_MS, set358Store, store358, type Store358, type TableState } from '../358/store';
import { SLOW } from '../hearts/store';
import { HIDDEN, expand, seatNamesFor, clockOf, type CardEndFrame, type CardStart, type CardStateFrame, type OnlineCardGame, type OnlineDeps } from './common';
import { renamed } from './common';

/** The exchange's two flights (358/store.ts swapped()): a tap before they land would be refused by the server. */
const SWAP_SHOW_MS = SWAP_FLY_MS * 2 + 150;


/** The server's turned view as the rules' state: the hidden cards as face-down stand-ins (the others' hands, my own
 *  while I declare, the musik until it turns up, the cards laid away, an exchange of the two others), no pattern. */
function toState(v: Record<string, unknown>): State358 {
  return {
    ...(v as unknown as State358),
    hands: (v.hands as unknown[]).map(expand),
    musik: expand(v.musik),
    musikCards: expand(v.musikCards),
    discards: expand(v.discards),
    swapLog: (v.swapLog as Array<Partial<SwapDone> & Pick<SwapDone, 'giver' | 'taker'>>).map((d) => ({ ...d, gave: d.gave ?? HIDDEN, got: d.got ?? HIDDEN })),
    pattern: [],
  };
}

export function createOnline358(deps: OnlineDeps): OnlineCardGame & { store: Store358 } {
  // the app's windows: OnlineDeps names Makao's and Kierki's only (the app passes any window name through)
  const app = deps;
  const empty: TableState = { game: null, selected: [], seconds: 0, deal: 0, collecting: false, swap: 0, lastSwap: null, playedByPlayer: 0, demo: false, names: null, startedAt: 0, endedAt: null, deadlineAt: null };
  let state: TableState = empty;
  const listeners = new Set<() => void>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let clock: ReturnType<typeof setInterval> | null = null;
  let offline: Store358 | null = null;
  /** No move before this (Date.now()): the deal or an exchange is still flying (the server waits for it too). */
  let busyUntil = 0;
  /** A move went out: no other until the server's answer (the next game.state). */
  let sent = false;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const after = (ms: number, fn: () => void) => {
    const h = setTimeout(() => {
      timers.delete(h);
      fn();
    }, ms * SLOW);
    timers.add(h);
  };
  const stopTimers = () => {
    for (const h of timers) clearTimeout(h);
    timers.clear();
    if (clock !== null) clearInterval(clock);
    clock = null;
  };
  /** The game when I may move now. */
  const ready = () => (state.game && !sent && Date.now() >= busyUntil ? state.game : null);
  const move = (m: Record<string, unknown>) => {
    sent = true;
    deps.onStep?.(`3-5-8 online: ${String(m.type)}`);
    deps.send(m);
  };
  const plain = (c: HCard) => ({ s: c.s, r: c.r });

  const store: Store358 = {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame() {
      // online the server deals
    },
    nextHand() {
      // online the next deal comes by itself
    },
    choose(c) {
      const g = ready();
      if (g && g.phase === 'declare' && g.declarer === 0) move({ type: 'declare', contract: c });
    },
    tap(card) {
      const g = ready();
      if (!g) return;
      if (g.phase === 'exchange' && g.pendingTrump?.taker === 0) return move({ type: 'back', card: plain(card) });
      if (g.phase === 'exchange' && !g.pendingTrump && g.swaps[0]?.giver === 0) return move({ type: 'give', card: plain(card) });
      if (g.phase === 'musik' && !g.musik.length && g.declarer === 0) {
        // the 4 cards to lay away are chosen here, then sent together
        const has = state.selected.some((c) => sameCard(c, card));
        if (has) set({ selected: state.selected.filter((c) => !sameCard(c, card)) });
        else if (state.selected.length < 4) set({ selected: [...state.selected, card] });
        return;
      }
      if (g.phase === 'play' && g.turn === 0 && legalCards(g, 0).some((c) => sameCard(c, card))) move({ type: 'play', card: plain(card) });
    },
    stopGiving() {
      const g = ready();
      if (g && g.phase === 'exchange' && !g.pendingTrump && g.swaps[0]?.giver === 0) move({ type: 'stop' });
    },
    discard() {
      const g = ready();
      if (g && g.phase === 'musik' && !g.musik.length && g.declarer === 0 && state.selected.length === 4) move({ type: 'discard', cards: state.selected.map(plain) });
    },
    stop: stopTimers,
    load() {
      // e2e only, offline
    },
    backdate() {
      // e2e only, offline
    },
  };

  return {
    game: 'three-five-eight',
    store,
    begin(start: CardStart) {
      stopTimers();
      offline ??= store358();
      busyUntil = 0;
      sent = false;
      state = { ...empty, deal: state.deal, swap: state.swap, names: seatNamesFor(start), ...clockOf(start) };
      set358Store(store);
      clock = setInterval(() => {
        const g = state.game;
        if (g && g.phase !== 'over' && g.phase !== 'hand-end') set({ seconds: state.seconds + 1 });
      }, 1000);
    },
    state(f: CardStateFrame) {
      const g = toState(f.state);
      const before = state.game;
      const deadlineAt = f.deadline !== undefined ? Date.now() + f.deadline : null;
      sent = false;
      const played = f.last && f.last.seat === 0 && f.last.what === 'play' ? 1 : 0;
      if (!before || g.handNo !== before.handNo) {
        // a new deal: the score window of the last one goes, the view throws the cards out
        app.closeWindow('t58_score');
        busyUntil = Date.now() + DEAL_MS * SLOW;
        set({ game: g, deal: state.deal + 1, selected: [], collecting: false, lastSwap: null, deadlineAt });
        return;
      }
      if (g.swapLog.length > before.swapLog.length) {
        // an exchange done: the view flies the two cards (face up only where I am one of the pair)
        busyUntil = Date.now() + SWAP_SHOW_MS * SLOW;
        set({ game: g, swap: state.swap + 1, lastSwap: g.swapLog[g.swapLog.length - 1]!, deadlineAt });
        return;
      }
      set({
        game: g, collecting: false, deadlineAt, playedByPlayer: state.playedByPlayer + played,
        ...(g.phase !== 'musik' && state.selected.length ? { selected: [] } : {}),
        ...(g.phase === 'over' ? { endedAt: Date.now() } : {}),
      });
      // a full trick slides to its taker at the end of the server's wait
      if (g.phase === 'trick-done') after(TRICK_MS - COLLECT_MS, () => state.game === g && set({ collecting: true }));
      if (g.phase === 'hand-end') app.openWindow('t58_score');
    },
    end(f: CardEndFrame) {
      const g = state.game;
      if (g && g.phase !== 'over') set({ game: { ...g, phase: 'over', turn: -1, ...(f.scores ? { scores: f.scores } : {}) }, endedAt: Date.now() });
      set({ deadlineAt: null });
      if (clock !== null) clearInterval(clock);
      clock = null;
      app.openWindow('t58_score');
    },
    rename(seat, name, bot, flag) {
      set({ names: renamed(state.names, seat, name, bot, flag) });
    },
    stop() {
      stopTimers();
      app.closeWindow('t58_score');
      if (offline) set358Store(offline);
      state = { ...empty, deal: state.deal, swap: state.swap };
    },
  };
}
